import {WriteButton} from '../common/buttons/WriteActions';
import {LocalizedDateField} from '../common/dates/LocalizedDateField';
import {getCurrencySymbol} from '../../common/currency';
import {Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, InputAdornment, MenuItem, Snackbar, TextField, useMediaQuery, useTheme} from '@mui/material';
import {useEffect, useMemo, useState} from 'react';
import useDebounce from '../../hooks/useDebounce';
import {AppTranslationFunction} from '@mybake/ui/hooks/useAppTranslation';
import {IFinanceTransaction, ISaveManualExpense} from '@mybake/ui/models/Finances/IFinance';
import {financeAPI} from '../../services/FinanceServices';
import {financeMonths} from '@mybake/ui/components/finances/financeMonths';
import {useCurrency} from '../../hooks/useCurrency';
import {getApiErrorKind, getApiErrorMessage, getApiFieldErrors, getApiTraceId} from '../../common/apiErrors';
import TraceIdFeedback from '../feedback/TraceIdFeedback';
import {LocalizedDecimalField} from '../common/inputs/LocalizedDecimalField';

interface Props {
    open: boolean;
    expense?: IFinanceTransaction | null;
    defaultAccountingMonth: string;
    onClose: () => void;
    onSaved: (message: string) => void;
    t: AppTranslationFunction;
}

const emptyForm = (month: string): ISaveManualExpense => ({
    amount: 0,
    accountingMonth: month,
    paidAt: new Date().toISOString().slice(0, 10),
    category: '',
    description: '',
});

const getExpenseId = (id: string) => id.startsWith('expense:') ? id.slice('expense:'.length) : id;

export const ManualExpenseDialog = ({open, expense, defaultAccountingMonth, onClose, onSaved, t}: Props) => {
    const {currency} = useCurrency();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [form, setForm] = useState<ISaveManualExpense>(() => emptyForm(defaultAccountingMonth));
    const [categorySearch, setCategorySearch] = useState('');
    const [submitError, setSubmitError] = useState('');
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [traceId, setTraceId] = useState('');
    const [serverToast, setServerToast] = useState('');
    const debouncedCategory = useDebounce(categorySearch, 250);
    const {data: categories = [], error: categoriesError} = financeAPI.useGetExpenseCategoriesQuery(debouncedCategory, {skip: !open});
    const [createExpense, createState] = financeAPI.useCreateManualExpenseMutation();
    const [updateExpense, updateState] = financeAPI.useUpdateManualExpenseMutation();
    const isSaving = createState.isLoading || updateState.isLoading;
    const [accountingYear = '', accountingMonth = ''] = form.accountingMonth.split('-');
    const accountingYearNumber = Number(accountingYear);
    const isAccountingMonthValid =
        /^\d{4}-\d{2}$/.test(form.accountingMonth) &&
        accountingYearNumber >= 1900 &&
        accountingYearNumber <= 2100 &&
        financeMonths.some(({value}) => value === accountingMonth);
    const isValid = form.amount > 0 && form.category.trim().length > 0 && isAccountingMonthValid && !!form.paidAt;

    const updateAccountingMonth = (year: string, month: string) => {
        setForm((value) => ({...value, accountingMonth: `${year}-${month}`}));
    };

    useEffect(() => {
        if (!open) return;
        setSubmitError('');
        setFieldErrors({});
        setTraceId('');
        setCategorySearch(expense?.category ?? '');
        setForm(
            expense
                ? {
                      amount: expense.amount,
                      accountingMonth: expense.accountingMonth?.slice(0, 7) ?? defaultAccountingMonth,
                      paidAt: expense.occurredAt.slice(0, 10),
                      category: expense.category,
                      description: expense.description,
                  }
                : emptyForm(defaultAccountingMonth),
        );
    }, [open, expense, defaultAccountingMonth]);

    const payload = useMemo<ISaveManualExpense>(
        () => ({
            ...form,
            category: form.category.trim(),
            accountingMonth: `${form.accountingMonth}-01`,
        }),
        [form],
    );

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitError('');
        setFieldErrors({});
        setTraceId('');
        try {
            if (expense) {
                await updateExpense({id: getExpenseId(expense.id), body: payload}).unwrap();
                onSaved(t('finance:expense_updated'));
            } else {
                await createExpense(payload).unwrap();
                onSaved(t('finance:expense_created'));
            }
            onClose();
        } catch (error) {
            const kind = getApiErrorKind(error);
            const apiFieldErrors = getApiFieldErrors(error, t);
            setFieldErrors(kind === 'validation' ? apiFieldErrors : {});
            if (kind === 'server') setServerToast(getApiErrorMessage(error, t));
            else if (kind !== 'validation' || Object.keys(apiFieldErrors).length === 0) setSubmitError(getApiErrorMessage(error, t));
            setTraceId(getApiTraceId(error));
        }
    };

    return <>
        <Dialog
            open={open}
            onClose={isSaving ? undefined : onClose}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    width: {xs: 'calc(100% - 16px)', sm: 720, md: 760},
                    maxWidth: {xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)'},
                    maxHeight: {xs: 'calc(100% - 16px)', sm: 'calc(100% - 48px)'},
                    m: {xs: 1, sm: 3},
                    borderRadius: 3,
                },
            }}
        >
            <DialogTitle sx={{px: {xs: 2, sm: 2.5}, py: 2}}>
                {expense ? t('finance:edit_expense') : t('finance:add_expense')}
            </DialogTitle>
            <DialogContent dividers sx={{px: {xs: 2, sm: 2.5}, py: 2}}>
                <Grid container spacing={{xs: 1.5, sm: 2}}>
                    {categoriesError && <Grid item xs={12}><Alert severity="warning">{getApiErrorMessage(categoriesError, t)}</Alert></Grid>}
                    <Grid item xs={12} sm={6}>
                        <LocalizedDecimalField
                            fullWidth
                            required
                            label={t('finance:amount')}
                            value={form.amount || undefined}
                            inputProps={{min: 0.01, step: 0.01}}
                            InputProps={{endAdornment: <InputAdornment position="end">{getCurrencySymbol(currency)}</InputAdornment>}}
                            onValueChange={(amount) => setForm((value) => ({...value, amount: amount ?? 0}))}
                            error={!!fieldErrors.amount}
                            helperText={fieldErrors.amount}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Autocomplete
                            freeSolo
                            options={categories}
                            value={form.category}
                            inputValue={categorySearch}
                            onChange={(_, value) => setForm((formValue) => ({...formValue, category: value ?? ''}))}
                            onInputChange={(_, value) => {
                                setCategorySearch(value);
                                setForm((formValue) => ({...formValue, category: value}));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    required
                                    label={t('finance:category')}
                                    inputProps={{...params.inputProps, maxLength: 100}}
                                    error={!!fieldErrors.category}
                                    helperText={fieldErrors.category || t('finance:category_hint')}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Grid container spacing={1}>
                            <Grid item xs={7}>
                                <TextField
                                    select
                                    fullWidth
                                    required
                                    label={t('finance:accounting_month')}
                                    value={accountingMonth}
                                    onChange={(event) => updateAccountingMonth(accountingYear, event.target.value)}
                                >
                                    {financeMonths.map(({value, label}) => (
                                        <MenuItem key={value} value={value}>
                                            {t(label)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={5}>
                                <TextField
                                    fullWidth
                                    required
                                    type="number"
                                    label={t('finance:year')}
                                    value={accountingYear}
                                    inputProps={{min: 1900, max: 2100, step: 1}}
                                    error={!!accountingYear && (accountingYearNumber < 1900 || accountingYearNumber > 2100 || !/^\d{4}$/.test(accountingYear))}
                                    helperText={fieldErrors.accountingMonth}
                                    onChange={(event) => updateAccountingMonth(event.target.value, accountingMonth)}
                                />
                            </Grid>
                            <Grid item xs={12} sx={{pt: '4px !important'}}>
                                <FormHelperText>{t('finance:accounting_month_hint')}</FormHelperText>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizedDateField
                            fullWidth
                            required
                            type="date"
                            label={t('finance:paid_at')}
                            value={form.paidAt}
                            InputLabelProps={{shrink: true}}
                            onChange={(event) => setForm((value) => ({...value, paidAt: event.target.value}))}
                            helperText={fieldErrors.paidAt || t('finance:paid_at_hint')}
                            error={!!fieldErrors.paidAt}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            multiline
                            minRows={isMobile ? 2 : 3}
                            label={t('finance:description')}
                            value={form.description}
                            inputProps={{maxLength: 500}}
                            onChange={(event) => setForm((value) => ({...value, description: event.target.value}))}
                        />
                    </Grid>
                    {submitError && (
                        <Grid item xs={12}>
                            <Alert severity="error">{submitError}<TraceIdFeedback traceId={traceId}/></Alert>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{px: {xs: 2, sm: 2.5}, py: 1.5, gap: 0.5}}>
                <Button onClick={onClose} disabled={isSaving}>
                    {t('common:cancel')}
                </Button>
                <WriteButton variant="contained" onClick={handleSubmit} disabled={!isValid || isSaving}>
                    {isSaving ? t('finance:saving') : t('common:save')}
                </WriteButton>
            </DialogActions>
        </Dialog>
        <Snackbar open={!!serverToast} autoHideDuration={6000} onClose={() => setServerToast('')} anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
            <Alert severity="error" onClose={() => setServerToast('')}>{serverToast}<TraceIdFeedback traceId={traceId}/></Alert>
        </Snackbar>
    </>;
};
